import { useMutation } from 'react-query';
import { v4 as uuidv4 } from 'uuid';

import { APIBaseChronos } from '@/api/hosts';
import useGetFetchConfig from '@/api/useGetFetchConfig';

interface StageProceduralFileParams {
  matterId: string;
  file: File;
}

interface StageProceduralFileResult {
  uploadId: string;
}

interface PresignedPostResponse {
  url: string;
  fields: Record<string, string>;
  expiresIn: number;
}

/**
 * Uploads a procedural-timeline file to the temporary S3 area and returns the
 * uploadId.
 */
export const useStageProceduralFile = () => {
  const { fetchConfigGET } = useGetFetchConfig();

  return useMutation<StageProceduralFileResult, Error, StageProceduralFileParams>(async ({ matterId, file }) => {
    const uploadId = uuidv4();

    const urlRes = await fetch(
      `${APIBaseChronos}/api/matter/${matterId}/procedural-timeline/upload-url?uploadId=${uploadId}&filename=${encodeURIComponent(file.name)}`,
      fetchConfigGET,
    );
    if (!urlRes.ok) throw new Error('Failed to get upload URL');
    const { url, fields }: PresignedPostResponse = await urlRes.json();

    const formData = new FormData();
    Object.entries(fields).forEach(([key, value]) => formData.append(key, value));
    formData.append('file', file);

    const s3Res = await fetch(url, { method: 'POST', body: formData });
    if (!s3Res.ok) throw new Error('Failed to upload file to storage');

    return { uploadId };
  });
};
